// Деньги: кому и сколько мы должны, и запись факта выплаты.
//
// Начисление считается по ПРИНЯТЫМ часам и посчитано на бэке при вердикте —
// здесь ничего не пересчитывается заново. Это важно: ставки меняются, а долг
// зафиксирован по ставке на момент начисления, и экран, который пересчитал бы
// его «сейчас», нарисовал бы другую цифру, чем та, о которой договаривались.
import { useEffect, useState } from 'preact/hooks';
import type { VistaBalanceRow, VistaLedgerRes } from '@weern/contracts';
import { api, type VistaMeRes as Me } from '../api';
import type { T } from '../i18n';
import { invalidate, useResource } from '../cache';

const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;

const fmtDate = (ms: number): string =>
  new Date(ms).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });


export function FinancePage({ me, t }: { me: Me; t: T }) {
  const [open, setOpen] = useState<VistaBalanceRow | null>(null);
  const [msg] = useState<string | null>(null);

  const res = useResource('balances', () => api.balances());
  const rows: VistaBalanceRow[] | null = res.data?.rows ?? null;
  const total = res.data?.totalOwedCents ?? 0;
  const load = async () => {
    invalidate('balances');
    await res.refresh();
  };

  if (rows === null) {
    return <div class="empty">{res.loading ? t('fin.loading') : t('fin.loadFailed')}</div>;
  }

  const contributors = rows.filter((r) => r.kind === 'contributor');
  const staff = rows.filter((r) => r.kind === 'staff');

  return (
    <>
      <div class="tiles">
        <div class="tile">
          <div class="n">{fmtMoney(total)}</div>
          <div class="t">{t('fin.tile.owed')}</div>
        </div>
        <div class="tile">
          <div class="n">{contributors.filter((r) => r.owedCents > 0).length}</div>
          <div class="t">{t('fin.tile.withDebt')}</div>
        </div>
        {/* Люди без ставки — это принятые часы, которые НЕ начислились. Тихо
            нулём их показывать нельзя: обнаружится при выплате, скандалом. */}
        <div class="tile">
          <div class="n">{contributors.filter((r) => r.rateCents == null && r.acceptedHours > 0).length}</div>
          <div class="t">{t('fin.tile.noRate')}</div>
        </div>
      </div>

      {msg && <div class="strip info">{msg}</div>}

      <h2 class="section">{t('fin.contributors')}</h2>
      <BalanceTable t={t} rows={contributors} onOpen={setOpen} />

      {me.role === 'manager' && staff.length > 0 && (
        <>
          <h2 class="section">{t('fin.staff')}</h2>
          <BalanceTable t={t} rows={staff} onOpen={setOpen} />
        </>
      )}

      {open && (
        <LedgerDialog
          t={t}
          row={open}
          canPay={me.role === 'manager'}
          onClose={() => setOpen(null)}
          onPaid={() => {
            setOpen(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function BalanceTable({
  t,
  rows,
  onOpen,
}: {
  t: T;
  rows: VistaBalanceRow[];
  onOpen: (r: VistaBalanceRow) => void;
}) {
  if (rows.length === 0) return <div class="empty">{t('fin.nobody')}</div>;
  return (
    <div class="tablewrap">
      <table>
        <thead>
          <tr>
            <th>{t('fin.th.who')}</th>
            <th>{t('fin.th.rate')}</th>
            <th>{t('fin.th.accepted')}</th>
            <th>{t('fin.th.owed')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.kind}:${r.id}`} class="row" onClick={() => onOpen(r)}>
              <td>
                {r.name ?? r.email}
                {r.name && <span class="sub">{r.email}</span>}
              </td>
              <td>
                {r.rateCents == null ? (
                  <span class="status-chip pend">{t('fin.noRate')}</span>
                ) : (
                  `${fmtMoney(r.rateCents)}/${t('pool.hours')}`
                )}
              </td>
              <td>{r.acceptedHours || '—'}</td>
              <td>{r.owedCents > 0 ? <b>{fmtMoney(r.owedCents)}</b> : fmtMoney(r.owedCents)}</td>
              <td class="muted">{t('fin.history')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PAGE_SIZE = 50;

function LedgerDialog({
  t,
  row,
  canPay,
  onClose,
  onPaid,
}: {
  t: T;
  row: VistaBalanceRow;
  canPay: boolean;
  onClose: () => void;
  onPaid: () => void;
}) {
  const [data, setData] = useState<VistaLedgerRes | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAdjust, setShowAdjust] = useState(false);

  const target = row.kind === 'contributor' ? { contributorId: row.id } : { userId: row.id };

  const loadFirstPage = () => {
    api
      .ledger({ ...target, limit: PAGE_SIZE, offset: 0 })
      .then(setData)
      .catch(() => setErr(t('fin.historyFailed')));
  };

  useEffect(loadFirstPage, [row.id]);

  const loadMore = async () => {
    if (!data) return;
    setLoadingMore(true);
    try {
      const next = await api.ledger({ ...target, limit: PAGE_SIZE, offset: data.rows.length });
      setData({ ...next, rows: [...data.rows, ...next.rows] });
    } catch {
      setErr(t('fin.moreFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  const pay = async () => {
    const cents = Math.round(Number(amount.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setErr(t('fin.err.amount'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // refKey собирается из получателя, суммы и минуты: двойной тап по кнопке
      // не заплатит дважды, а осознанная вторая выплата в другую минуту пройдёт.
      const stamp = new Date().toISOString().slice(0, 16);
      const res = await api.payout({
        ...target,
        amountCents: cents,
        refKey: `pay:${row.kind}:${row.id}:${cents}:${stamp}`,
      });
      if (res.duplicate) setErr(t('fin.err.duplicatePayout'));
      else {
        setAmount('');
        onPaid();
      }
    } catch {
      setErr(t('fin.err.notSaved'));
    } finally {
      setBusy(false);
    }
  };

  const hasMore = data != null && data.rows.length < data.total;

  return (
    <dialog open>
      <h3>{row.name ?? row.email}</h3>
      <p class="muted">
        {t('fin.owed')} <b>{fmtMoney(row.owedCents)}</b>
        {row.rateCents != null && t('fin.rateSuffix', { rate: fmtMoney(row.rateCents) })}
      </p>

      {canPay && (
        <div class="row">
          <label class="field">
            <span>{t('fin.payAmount')}</span>
            <input value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} />
          </label>
          <button type="button" class="btn-primary" disabled={busy} onClick={() => void pay()}>
            {t('fin.pay')}
          </button>
          <button type="button" class="btn-ghost" onClick={() => setShowAdjust((v) => !v)}>
            {showAdjust ? t('fin.adjust.hide') : t('fin.adjust')}
          </button>
        </div>
      )}
      {err && <div class="strip bad">{err}</div>}

      {canPay && showAdjust && (
        <AdjustForm
          t={t}
          target={target}
          kind={row.kind}
          rowId={row.id}
          onDone={() => {
            setShowAdjust(false);
            loadFirstPage();
            onPaid();
          }}
        />
      )}

      <div class="tablewrap">
        <table>
          <thead>
            <tr>
              <th>{t('fin.th.when')}</th>
              <th>{t('fin.th.what')}</th>
              <th>{t('fin.th.amount')}</th>
              <th>{t('fin.th.note')}</th>
              <th>{t('fin.th.by')}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((l) => (
              <tr key={l.id}>
                <td>{fmtDate(l.createdAt)}</td>
                <td>{t(`fin.type.${l.type}`)}</td>
                {/* Знак сохраняем как в базе (начисление минусом, выплата
                    плюсом): плюс/минус в списке движений — это и есть смысл
                    операции, прятать его модулем здесь неверно. */}
                <td class={l.amountCents < 0 ? 'money-neg' : 'money-pos'}>
                  {l.amountCents < 0 ? '−' : '+'}
                  {fmtMoney(Math.abs(l.amountCents))}
                </td>
                <td class="reason">{l.note ?? '—'}</td>
                <td class="muted">{l.createdByName ?? '—'}</td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={5} class="empty">
                  {t('fin.noMoves')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button type="button" class="btn-ghost" disabled={loadingMore} onClick={() => void loadMore()}>
          {loadingMore ? t('fin.loading') : t('fin.more', { n: data!.total - data!.rows.length })}
        </button>
      )}

      <button type="button" class="btn-ghost" onClick={onClose}>
        {t('fin.close')}
      </button>
    </dialog>
  );
}

function AdjustForm({
  t,
  target,
  kind,
  rowId,
  onDone,
}: {
  t: T;
  target: { contributorId?: string; userId?: string };
  kind: 'contributor' | 'staff';
  rowId: string;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    const cents = Math.round(Number(amount.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents === 0) {
      setErr(t('fin.err.adjustAmount'));
      return;
    }
    if (reason.trim().length < 3) {
      // Причина обязательна на бэке (min 3 символа) — без неё ошибку начисления
      // потом никто не восстановит: запись без объяснения хуже, чем её отсутствие.
      setErr(t('fin.err.adjustReason'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const stamp = new Date().toISOString().slice(0, 16);
      const res = await api.adjust({
        ...target,
        amountCents: cents,
        reason: reason.trim(),
        refKey: `adj:${kind}:${rowId}:${cents}:${stamp}`,
      });
      if (res.duplicate) setErr(t('fin.err.duplicateAdjust'));
      else {
        setAmount('');
        setReason('');
        onDone();
      }
    } catch {
      setErr(t('fin.err.notSaved'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="panel">
      <label class="field">
        <span>{t('fin.adjust.amount')}</span>
        <input value={amount} placeholder="-5.00" onInput={(e) => setAmount((e.target as HTMLInputElement).value)} />
      </label>
      <label class="field">
        <span>{t('fin.adjust.reason')}</span>
        <input value={reason} onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
      </label>
      {err && <div class="strip bad">{err}</div>}
      <button type="button" class="btn-primary" disabled={busy} onClick={() => void submit()}>
        {busy ? t('fin.saving') : t('fin.adjust.apply')}
      </button>
    </div>
  );
}
